import React, { useMemo, useState } from 'react';
import { Clock, Coffee, UtensilsCrossed, User, GraduationCap, School } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';

/**
 * Read-only weekly timetable renderer shared by the Teacher & Student
 * "My Timetable" portal pages.
 *
 * - Desktop (>=768px): classic weekly grid (period rows x day columns).
 * - Mobile  (<768px):  day tabs (Mon..Fri) + a day-by-day card list.
 *
 * Props:
 *   config  : { days:[{key,label,active}], periods:[{seq,label,start,end,is_break,days?}] }
 *   entries : [{ day, period_seq, subject_name, teacher_name, class_name, section_name, uv_group, label }]
 *   mode    : 'teacher' | 'student'
 */

// Stable, curated palette (full static class strings so Tailwind keeps them).
const PALETTES = [
  { chip: 'bg-indigo-500/12 text-indigo-700 dark:text-indigo-300 border-indigo-500/30', dot: 'bg-indigo-500' },
  { chip: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300 border-emerald-500/30', dot: 'bg-emerald-500' },
  { chip: 'bg-amber-500/12 text-amber-700 dark:text-amber-300 border-amber-500/30', dot: 'bg-amber-500' },
  { chip: 'bg-rose-500/12 text-rose-700 dark:text-rose-300 border-rose-500/30', dot: 'bg-rose-500' },
  { chip: 'bg-sky-500/12 text-sky-700 dark:text-sky-300 border-sky-500/30', dot: 'bg-sky-500' },
  { chip: 'bg-violet-500/12 text-violet-700 dark:text-violet-300 border-violet-500/30', dot: 'bg-violet-500' },
  { chip: 'bg-teal-500/12 text-teal-700 dark:text-teal-300 border-teal-500/30', dot: 'bg-teal-500' },
  { chip: 'bg-orange-500/12 text-orange-700 dark:text-orange-300 border-orange-500/30', dot: 'bg-orange-500' },
  { chip: 'bg-cyan-500/12 text-cyan-700 dark:text-cyan-300 border-cyan-500/30', dot: 'bg-cyan-500' },
  { chip: 'bg-fuchsia-500/12 text-fuchsia-700 dark:text-fuchsia-300 border-fuchsia-500/30', dot: 'bg-fuchsia-500' },
  { chip: 'bg-lime-500/12 text-lime-700 dark:text-lime-300 border-lime-500/30', dot: 'bg-lime-500' },
  { chip: 'bg-blue-500/12 text-blue-700 dark:text-blue-300 border-blue-500/30', dot: 'bg-blue-500' },
];

const NEUTRAL = { chip: 'bg-muted text-muted-foreground border-border', dot: 'bg-muted-foreground/40' };

function subjectColor(key) {
  const s = String(key || '');
  if (!s) return NEUTRAL;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PALETTES[h % PALETTES.length];
}

const periodRunsOn = (period, dayKey) => {
  const pd = period?.days;
  if (!pd || pd.length === 0) return true;
  return pd.includes(dayKey);
};

const timeLabel = (p) => (p.start && p.end ? `${p.start} – ${p.end}` : p.label || '');

// Short mobile-tab label from a day's label ("Monday" -> "Mon").
const shortDay = (label, key) => (label ? label.slice(0, 3) : (key || '').slice(0, 3).replace(/^\w/, (c) => c.toUpperCase()));

export const TimetableReadOnly = ({ config, entries = [], mode = 'student' }) => {
  const days = useMemo(
    () => (config?.days || []).filter((d) => d.active !== false),
    [config],
  );
  const periods = useMemo(
    () => [...(config?.periods || [])].sort((a, b) => (a.seq || 0) - (b.seq || 0)),
    [config],
  );

  // Lookup: entryByDayPeriod[dayKey][seq] = entry
  const lookup = useMemo(() => {
    const m = {};
    for (const e of entries) {
      if (!e.day || e.period_seq == null) continue;
      (m[e.day] ||= {})[e.period_seq] = e;
    }
    return m;
  }, [entries]);

  // Default selected mobile day = today if it is an active teaching day, else first.
  const todayKey = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][new Date().getDay()];
  const initialDay = days.some((d) => d.key === todayKey) ? todayKey : days[0]?.key;
  const [activeDay, setActiveDay] = useState(initialDay);

  const secondaryOf = (e) => {
    if (!e) return null;
    if (mode === 'teacher') {
      const cls = e.class_name || '';
      const sec = e.section_name ? ` / ${e.section_name}` : '';
      return (cls + sec) || null;
    }
    return e.teacher_name || null;
  };

  const SecondaryIcon = mode === 'teacher' ? School : User;

  if (!days.length || !periods.length) {
    return (
      <div className="text-sm text-muted-foreground py-10 text-center" data-testid="timetable-empty-config">
        No bell schedule configured for this academic year.
      </div>
    );
  }

  return (
    <div data-testid="timetable-readonly">
      {/* ================= DESKTOP GRID (md and up) ================= */}
      <div className="hidden md:block overflow-x-auto rounded-lg border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="sticky left-0 z-10 bg-muted/50 text-left font-semibold text-xs uppercase tracking-wider text-muted-foreground px-3 py-2.5 w-36 border-b border-border">
                Period
              </th>
              {days.map((d) => (
                <th
                  key={d.key}
                  className="text-left font-semibold text-xs uppercase tracking-wider text-muted-foreground px-3 py-2.5 border-b border-l border-border"
                  data-testid={`tt-col-${d.key}`}
                >
                  {d.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map((p) => {
              if (p.is_break) {
                const isLunch = /lunch/i.test(p.label || '');
                const BreakIcon = isLunch ? UtensilsCrossed : Coffee;
                return (
                  <tr key={p.seq} data-testid={`tt-break-row-${p.seq}`}>
                    <td colSpan={days.length + 1} className="border-b border-border bg-muted/30 px-3 py-1.5">
                      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                        <BreakIcon className="w-3.5 h-3.5" />
                        <span>{p.label}</span>
                        <span className="text-muted-foreground/70">{timeLabel(p)}</span>
                      </div>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={p.seq} className="hover:bg-accent/30 transition-colors">
                  <td className="sticky left-0 z-10 bg-card px-3 py-2 align-top border-b border-border">
                    <div className="font-medium text-foreground text-xs">{p.label}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />{timeLabel(p)}
                    </div>
                  </td>
                  {days.map((d) => {
                    const runs = periodRunsOn(p, d.key);
                    const e = lookup[d.key]?.[p.seq];
                    return (
                      <td key={d.key} className="px-2 py-1.5 align-top border-b border-l border-border" data-testid={`tt-cell-${d.key}-${p.seq}`}>
                        {!runs ? (
                          <span className="text-muted-foreground/30 text-xs">—</span>
                        ) : e ? (
                          <CellChip entry={e} secondary={secondaryOf(e)} SecondaryIcon={SecondaryIcon} />
                        ) : (
                          <span className="inline-flex text-[11px] text-muted-foreground/50 italic px-1">Free</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ================= MOBILE DAY-BY-DAY (below md) ================= */}
      <div className="md:hidden">
        <Tabs value={activeDay} onValueChange={setActiveDay}>
          <TabsList
            className="grid w-full h-auto p-1 gap-1"
            style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}
            data-testid="tt-day-tabs"
          >
            {days.map((d) => (
              <TabsTrigger
                key={d.key}
                value={d.key}
                className="min-h-[44px] text-xs font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                data-testid={`tt-day-tab-${d.key}`}
              >
                {shortDay(d.label, d.key)}
              </TabsTrigger>
            ))}
          </TabsList>

          {days.map((d) => (
            <TabsContent key={d.key} value={d.key} className="mt-3 space-y-2" data-testid={`tt-day-panel-${d.key}`}>
              {periods.map((p) => {
                if (p.is_break) {
                  const isLunch = /lunch/i.test(p.label || '');
                  const BreakIcon = isLunch ? UtensilsCrossed : Coffee;
                  return (
                    <div
                      key={p.seq}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/40 text-xs font-medium text-muted-foreground"
                      data-testid={`tt-m-break-${d.key}-${p.seq}`}
                    >
                      <BreakIcon className="w-3.5 h-3.5" />
                      <span>{p.label}</span>
                      <span className="ml-auto text-muted-foreground/70">{timeLabel(p)}</span>
                    </div>
                  );
                }
                const runs = periodRunsOn(p, d.key);
                if (!runs) return null;
                const e = lookup[d.key]?.[p.seq];
                const color = e ? subjectColor(e.subject_id || e.label || e.subject_name) : NEUTRAL;
                const secondary = secondaryOf(e);
                return (
                  <div
                    key={p.seq}
                    className={`rounded-lg border p-3 flex items-stretch gap-3 ${e ? 'bg-card border-border shadow-[var(--shadow-tight)]' : 'bg-muted/20 border-dashed border-border'}`}
                    data-testid={`tt-m-slot-${d.key}-${p.seq}`}
                  >
                    <div className={`w-1 rounded-full ${e ? color.dot : 'bg-transparent'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span className="font-semibold text-foreground">{p.label}</span>
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{timeLabel(p)}</span>
                      </div>
                      {e ? (
                        <div className="mt-1.5">
                          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-sm font-medium ${color.chip}`}>
                            {e.label || e.subject_name}
                          </span>
                          {secondary && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1.5">
                              <SecondaryIcon className="w-3.5 h-3.5" />
                              <span className="truncate">{secondary}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1 text-sm text-muted-foreground/50 italic">Free</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
};

// Compact colored chip used inside the desktop grid cells.
const CellChip = ({ entry, secondary, SecondaryIcon }) => {
  const color = subjectColor(entry.subject_id || entry.label || entry.subject_name);
  return (
    <div className="flex flex-col gap-1">
      <span className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs font-medium ${color.chip}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${color.dot}`} />
        <span className="truncate max-w-[120px]">{entry.label || entry.subject_name}</span>
      </span>
      {secondary && (
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground pl-1 truncate max-w-[140px]">
          <SecondaryIcon className="w-3 h-3 flex-shrink-0" />{secondary}
        </span>
      )}
    </div>
  );
};

export default TimetableReadOnly;
